import moment from "moment";
import FileService from "services/File.service";
import { authHeader } from "services/base/Ajax.service";
import { BASE_URL, DATETIME_FORMAT_ISO } from "shared/constants";
import { ALERT_ACCESSOR_LIST, ALERT_ACCESSOR_SYSTEM } from "web/constants";

export const pluralize = (string, count) => {
  if (Number(count) === 1) {
    return string;
  }
  // Unaltered special cases
  if (/[^aeiouh]ee[pr]$/i.test(string) || string.endsWith("ies")) {
    return string;
  }
  // Latin / Greek roots
  if (string.endsWith("us")) {
    return string.replace(/us$/i, "i");
  }
  if (string.endsWith("is")) {
    return string.replace(/is$/i, "es");
  }
  if (string.endsWith("man")) {
    return string.replace(/(m)an$/i, "$1en");
  }
  if (string.endsWith("person")) {
    return string.replace(/(p)erson$/i, "$1eople");
  }
  // Standard cases
  if (/[^aeiou]y$/i.test(string)) {
    return string.replace(/y$/i, "ies");
  }
  if (/[shxz]$/i.test(string)) {
    if (/[^s][s]$/i.test(string)) {
      return string + "ses";
    }
    if (/[^z][z]$/i.test(string)) {
      return string + "zes";
    }
    return string + "es";
  }

  return string + "s";
};

export const getInitials = (string) => {
  const words = string?.split(" ");
  let initials = "";
  let count = 0;
  words?.forEach((word) => {
    if (count < 2) {
      initials += word.charAt(0);
      count++;
    }
  });
  return initials;
};

export const getOrganizationColor = (color) => {
  // If color is a valid hex value, return it directly
  if (color && /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
    return color;
  }
  // Return default hex color if no valid hex provided
  return "#1554A3";
};

export const getTintedOrganizationColor = (colorInput, value) => {
  let color = getOrganizationColor(colorInput);
  if (color.startsWith("#")) {
    color = color.substring(1);
  }

  const rgb = Number.parseInt(color, 16);
  let red = (rgb >> 16) & 0xff;
  let green = (rgb >> 8) & 0xff;
  let blue = rgb & 0xff;

  red = Math.max(0, red - value);
  green = Math.max(0, green - value);
  blue = Math.max(0, blue - value);

  red = red.toString(16).padStart(2, "0");
  green = green.toString(16).padStart(2, "0");
  blue = blue.toString(16).padStart(2, "0");

  return `#${red}${green}${blue}`;
};

/**
 * Returns a solid color that appears the same as if the given color
 * had been modified with the given opacity on a white background.
 */
export const getOpaqueColor = (hexColor, opacity = 0.2) => {
  const red = Math.floor(
    Number.parseInt(hexColor.slice(1, 3), 16) * opacity + 0xff * (1 - opacity),
  );
  const green = Math.floor(
    Number.parseInt(hexColor.slice(3, 5), 16) * opacity + 0xff * (1 - opacity),
  );
  const blue = Math.floor(
    Number.parseInt(hexColor.slice(5, 7), 16) * opacity + 0xff * (1 - opacity),
  );

  return `rgb(${red}, ${green}, ${blue})`;
};

export const formatDatetime = (datetime) => {
  if (!datetime) {
    return null;
  }
  return moment(datetime).format(DATETIME_FORMAT_ISO);
};

export const getPreview = (text) => {
  if (!text) {
    return "";
  }
  const chars = [...text];
  if (chars.length < 100) {
    return text;
  } else {
    return chars.slice(0, 100).join("") + "...";
  }
};

export const handleLongIconNames = (text) => {
  if (text.length < 100) {
    return text;
  } else {
    return text.slice(0, 100) + "...";
  }
};

export const removeAssociationsFromList = (list = []) => {
  return list.map((obj) => {
    const copy = { ...obj };
    delete copy._associations;
    return copy;
  });
};

export const removeAssociations = (obj) => {
  const copy = { ...obj };
  delete copy._associations;
  return copy;
};

export const getPersonIdsFromSelectedSystems = (
  selectedSystemIds,
  teamList,
  personsByTeam,
  organizationSystemMap,
) => {
  const dependencies = selectedSystemIds.flatMap((systemId) => {
    const system = organizationSystemMap?.get(systemId);
    const deps = system?._associations.organizationSystemDependencies || [];
    return deps;
  });

  let finalTeamIds = [];

  const hasOrganizationId = dependencies.some(
    (dep) => dep._associations?.organizationId,
  );

  if (hasOrganizationId) {
    const allTeamIds = teamList.map((team) => team.teamId);
    finalTeamIds = allTeamIds;
  }

  const teamIdsInDeps = dependencies
    .filter((dep) => dep._associations?.teamId)
    .map((dep) => dep._associations.teamId);

  if (teamIdsInDeps.length > 0) {
    const combined = new Set([...finalTeamIds, ...teamIdsInDeps]);
    finalTeamIds = Array.from(combined);
  }

  const personIds = finalTeamIds.flatMap((teamId) => {
    const users = personsByTeam[teamId] || [];
    return users.map((user) => user.personId);
  });

  return { personIds, teamIds: finalTeamIds };
};

export const attachSignedFileUrls = async (data) => {
  if (Array.isArray(data)) {
    return Promise.all(data.map(attachSignedFileUrls));
  }

  if (typeof data !== "object" || data === null) {
    return data;
  }

  const updated = { ...data };

  if (updated.fileId) {
    try {
      const signedRes = await FileService.getSignedFileUrl(updated.fileId);
      // eslint-disable-next-line require-atomic-updates
      updated.signedFileUrl = signedRes.payload;
    } catch (err) {
      if (__DEV__) {
        console.warn(
          `Failed to attach signed URL for fileId ${updated.fileId}:`,
          err.message,
        );
      }
    }
  }

  const updatedCopy = { ...updated };
  await Promise.all(
    Object.keys(updatedCopy).map(async (key) => {
      const value = updatedCopy[key];

      if (typeof value === "object" && value !== null) {
        updated[key] = await attachSignedFileUrls(value);
      }
    }),
  );

  return updated;
};

export const uploadImage = async (image) => {
  try {
    const file = {
      uri: image.path,
      type: image.mime,
      name: image.filename || "image.jpg",
    };

    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", file.name);
    formData.append("type", "1");

    const response = await fetch(`${BASE_URL}/file/upload`, {
      method: "POST",
      body: formData,
      headers: {
        ...(await authHeader()),
      },
    });

    const data = await response?.json();
    const fileId = data?.payload?.fileId;

    const signedFileUrl = await FileService.getSignedFileUrl(fileId);

    return { fileId, signedFileUrl };
  } catch (err) {
    if (__DEV__) {
      console.warn("Error uploading image", err);
    }
  }
};

export const uploadFileWeb = async (file, type = 1, isDownloadable = true) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", file.name);
    formData.append("type", String(type));
    formData.append("isDownloadable", isDownloadable);

    const response = await fetch(`${BASE_URL}/file/upload`, {
      method: "POST",
      body: formData,
      headers: {
        ...(await authHeader()),
      },
    });

    const data = await response.json();
    const fileId = data.payload.fileId;

    const signedFileUrl = await FileService.getSignedFileUrl(fileId);

    return { fileId, signedFileUrl };
  } catch (err) {
    if (__DEV__) {
      console.warn("Error uploading image", err);
    }
  }
};

export const isAnyArrayEmpty = (arrays) => {
  return arrays.some((arr) => !Array.isArray(arr) || arr.length === 0);
};

export function timeSince(dateInput) {
  const date = new Date(dateInput);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 5) {
    return "just now";
  }
  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }

  const weeks = Math.floor(days / 7);
  if (weeks < 4) {
    return `${weeks}w ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months}mo ago`;
  }

  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

export function buildGoogleMapsSearchUrl(inputString) {
  if (!inputString) {
    return "";
  }
  if (inputString.startsWith("http://") || inputString.startsWith("https://")) {
    return inputString;
  }
  const trimmed = inputString.trim();
  let encoded = encodeURIComponent(trimmed);
  encoded = encoded.replace(/%20/g, "+");
  encoded = encoded.replace(/\|/g, "%7C");
  return `https://www.google.com/maps/search/?api=1&query=${encoded}`;
}

export const getDependentTeamsForSystem = (
  system,
  teamList,
  locationList = [],
) => {
  const deps = system?._associations?.organizationSystemDependencies || [];

  const teamIds = deps
    .filter((dependent) => dependent?._associations?.teamId)
    .map((dependent) => dependent._associations.teamId);

  const locationIds = deps
    .filter((dependent) => dependent?._associations?.locationId)
    .map((dependent) => dependent._associations.locationId);

  const hasOrg = deps.some(
    (dependent) => dependent?._associations?.organizationId,
  );

  const organizationId = hasOrg
    ? deps.find((dependent) => dependent?._associations?.organizationId)
        ?._associations?.organizationId
    : null;

  const finalTeamIds = hasOrg ? teamList.map((team) => team.teamId) : teamIds;
  const finalLocationIds = hasOrg
    ? locationList.map((location) => location.locationId)
    : locationIds;

  const teams = finalTeamIds
    .map((id) => teamList.find((team) => team.teamId === id))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  const locations = finalLocationIds
    .map((id) => locationList.find((location) => location.locationId === id))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    teams,
    teamIds: finalTeamIds,
    locations,
    locationIds: finalLocationIds,
    organizationId,
    hasOrganizationDependency: hasOrg,
  };
};

/*
 * type is an ENUM of
 * "system" for platforms,
 * "team" for teams, and
 * "location" for locations
 *
 * Systems use the pk "organizationSystemId" causing us to handle the accessor differently
 */
export const getRelatedAlerts = (type, id, sortedAllAlerts) => {
  if (!type || !id || !sortedAllAlerts) {
    return [];
  }

  if (!ALERT_ACCESSOR_LIST.includes(type)) {
    console.warn(`Invalid alert accessor type: ${type}`);
    return [];
  }

  if (type !== ALERT_ACCESSOR_SYSTEM) {
    return Array.from(sortedAllAlerts.values()).filter(
      (alert) =>
        alert._associations?.[`${type}AlertMap`]?.some(
          (mapItem) => mapItem[`${type}Id`] === id,
        ) && alert.isActive === 1,
    );
  }
  return Array.from(sortedAllAlerts.values()).filter(
    (alert) =>
      alert._associations?.systemAlertMap?.some(
        (mapItem) => mapItem.organizationSystemId === id,
      ) && alert.isActive === 1,
  );
};
